import { css } from '@emotion/react';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button } from 'react-bootstrap';
import debounce from 'lodash/debounce';
import t from 'react-translate';
import { useAppDispatch } from 'redux/store';
import { usePaginatedApi } from 'shared/hooks/use-paginated-api';
import NvTable, { Column } from 'shared/components/nv-table';
import NvCheckbox from 'shared/components/inputs/nv-checkbox';
import MentoringProgramContext, { MentoringProgramHomeUIContext } from 'athena/components/mentoring-program/context';
import AthenaTextInput, { AthenaTextInputType } from 'athena/components/text-input';
import { getMentoringProgramRecruitmentApplications } from 'redux/actions/mentoring-program-recruitments';
import { RecruitmentApplication } from 'redux/schemas/models/mentoring-program-recruitments';
import { getMentoringProgramRecruitmentApplicationsList } from 'redux/selectors/mentorship-program-recruitments';
import { getMentoringProgramAliases } from 'redux/selectors/mentoring-programs';
import { ColumnID } from './recruitment-row/types';
import RecruitmentRow from './recruitment-row';


type RecruitmentProps = {
  scrollEnabled: boolean;
  onScroll: React.ComponentProps<'div'>['onScroll'];
};

const Recruitment = ({
  scrollEnabled,
  onScroll,
}: RecruitmentProps) => {
  const styles = css`
    .bulk-edit-row {
      height: 70px;
    }

    .table-body {
      overflow-y: ${scrollEnabled ? 'auto' : 'hidden'};
    }
  `;

  const dispatch = useAppDispatch();
  const scrollRef = useRef<HTMLDivElement>();

  const { countState, mentoringProgram } = useContext(MentoringProgramContext);
  const recruitmentCount = countState.result?.recruitmentApplicationsCount.mentor;
  const { id: programId, recruitments } = mentoringProgram || {};
  const { withOverflowFlexGrowClass } = useContext(MentoringProgramHomeUIContext);

  const mentoringProgramAliases = useSelector((state) => getMentoringProgramAliases(state, programId));

  const bodyProps = {
    onScroll,
    className: `table-body ${withOverflowFlexGrowClass}`,
  };

  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
  const [sorting, setSorting] = useState([{
    id: ColumnID.DATE_SUBMITTED,
    desc: false,
  }]);
  const [textSearch, setTextSearch] = useState('');
  const debounceSetTextSearch = useCallback(
    debounce(setTextSearch, 500),
    [],
  );

  const params = useMemo(() => {
    const sortingObject: any = {};
    const currentSorting = sorting[0];

    if (currentSorting) {
      sortingObject.sortBy = currentSorting.id;
      sortingObject.sortDesc = currentSorting.desc;
    }

    return {
      textSearch: textSearch?.trim() || undefined,
      ...sortingObject,
    };
  }, [sorting, textSearch]);

  const {
    reset,
    result,
    loadMore,
    isLoading,
    page,
  } = usePaginatedApi(
    (p) => dispatch(
      getMentoringProgramRecruitmentApplications({
        programId,
        recruitmentId: recruitments?.mentor?.id,
        ...p,
      }),
    ).then(actions => actions.payload),
    params,
    getMentoringProgramRecruitmentApplicationsList,
  );

  const getData = () => {
    if (isLoading && page === 1) return [];
    return result ?? [];
  };

  const isUserSelected = (userId: number) => selectedUsers.some(({ id }) => id === userId);

  const applicationTableColumns: Column<RecruitmentApplication>[] = useMemo(() => [
    {
      id: ColumnID.CHECKBOX,
      headerContent: (
        <NvCheckbox
          name='select-all'
          label=''
          checked={recruitmentCount > 0 && selectedUsers.length === recruitmentCount}
          // TODO: talk to Backend about how to implement select all functionality
          onChange={() => {}}
        />
      ),
      accessorFn: (application) => application,
      gridTemplateColumn: '40px',
    },
    {
      id: ColumnID.NAME,
      enableSorting: true,
      header: t.MENTORING_PROGRAMS.RECRUITMENT.COLUMNS.NAME(),
      accessorFn: (application) => application.user,
      gridTemplateColumn: '250px',
    },
    {
      id: ColumnID.DATE_SUBMITTED,
      enableSorting: true,
      header: t.MENTORING_PROGRAMS.RECRUITMENT.COLUMNS.DATE_SUBMITTED(),
      accessorFn: (application) => application.createdAt,
      gridTemplateColumn: '1fr',
    },
    {
      id: ColumnID.ADD_BUTTON,
      accessorFn: (application) => application,
    },
  ], [recruitmentCount, selectedUsers.length]);

  return (
    <div css={styles} className={`d-flex flex-column ${withOverflowFlexGrowClass}`}>
      <div className='d-flex mb-4'>
        <AthenaTextInput
          icon='search'
          type={AthenaTextInputType.SEARCH}
          value={textSearch}
          showLabel={false}
          className='w-100'
          onChange={({ target: { value } }) => debounceSetTextSearch(value)}
          placeholder={t.MENTORING_PROGRAMS.RECRUITMENT.SEARCH_PLACEHOLDER()}
          // TODO: add dataQa
          // dataQa={config.pendo.athena.mentorshipProgram.recruitment.searchApplicationInputField}
        />
      </div>
      <div className='bulk-edit-row d-flex align-items-center justify-content-between px-4 bg-color-gray-7 border-radius-2'>
        <div className='text-small color-gray-1 semi-bold'>
          {selectedUsers.length === 0 && t.MENTORING_PROGRAMS.RECRUITMENT.SELECT_CANDIDATES(mentoringProgramAliases)}
          {selectedUsers.length > 0 && t.MENTORING_PROGRAMS.RECRUITMENT.M_OUT_OF_N_SELECTED(selectedUsers.length, recruitmentCount)}
        </div>
        <Button
          variant='primary'
          disabled={!selectedUsers.length}
        >
          {t.MENTORING_PROGRAMS.RECRUITMENT.ADD_AS_MENTORS(mentoringProgramAliases)}
        </Button>
      </div>
      <NvTable<RecruitmentApplication>
        data={getData()}
        onSortingChange={setSorting}
        columns={applicationTableColumns}
        className={`d-flex flex-column ${withOverflowFlexGrowClass}`}
        RowComponent={RecruitmentRow}
        bodyProps={bodyProps}
        state={{
          sorting,
        }}
        meta={{
          isUserSelected,
          setSelectedUsers,
        }}
      />
    </div>
  );
};

export default Recruitment;
